import axios, { InternalAxiosRequestConfig } from 'axios'
import { getToken, waitForTokenGetter } from './token-manager'
import { notifySessionExpired } from './session-expired'

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://movieception.com/api',
})

// Marks a request that intentionally went out without a token (no getter was
// available in time). A 401 on one of these means "auth was not ready", NOT
// "the session died", so the response interceptor must not relaunch Auth0.
interface TaggedRequestConfig extends InternalAxiosRequestConfig {
  _sentWithoutToken?: boolean
}

// After an auto-relogin is triggered we set this timestamp. A second 401 within
// the window must not redirect again - that is the loop we are breaking.
const RELOGIN_GUARD_KEY = 'auth-relogin-attempt'
const RELOGIN_GUARD_MS = 30_000

const reloginRecentlyAttempted = (): boolean => {
  try {
    const raw = sessionStorage.getItem(RELOGIN_GUARD_KEY)
    if (!raw) return false
    return Date.now() - Number(raw) < RELOGIN_GUARD_MS
  } catch {
    return false
  }
}

const markReloginAttempt = (): void => {
  try {
    sessionStorage.setItem(RELOGIN_GUARD_KEY, String(Date.now()))
  } catch {
    /* best effort */
  }
}

/**
 * Request interceptor - attaches the Bearer token.
 *
 * If no token is available yet we wait briefly for auth to finish rehydrating
 * (fresh tab) instead of firing the request bare. Only if it never becomes
 * ready do we send without a token, and we TAG the request so the response
 * side knows a resulting 401 is "not ready", not "session dead".
 */
instance.interceptors.request.use(
  async (config: TaggedRequestConfig) => {
    if (config.headers?.Authorization) return config

    let token = await getToken()
    if (!token) {
      // Might just be mid-rehydration on a fresh tab - give it a moment.
      const becameReady = await waitForTokenGetter()
      if (becameReady) token = await getToken()
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    } else {
      // Genuinely no auth (logged out, or never became ready). Let the request
      // proceed anonymously but tag it so a 401 does not trigger a redirect.
      config._sentWithoutToken = true
    }
    return config
  },
  (error) => Promise.reject(error)
)

/**
 * Response interceptor - handles 401s globally.
 *
 * Only a 401 on a request that actually CARRIED a token means the session is
 * dead (renewal failed / token revoked) - that is when we hand off to
 * AuthContext to re-authenticate. A 401 on a no-token request is just a
 * premature/anonymous call and must fail quietly, or we get the redirect loop.
 * The guard timestamp additionally prevents repeated redirects.
 */
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error.config as TaggedRequestConfig | undefined
    if (error.response?.status === 401) {
      if (config?._sentWithoutToken) {
        // Auth was not ready; this is not a dead session. Fail quietly.
      } else if (reloginRecentlyAttempted()) {
        // We already bounced through Auth0 very recently and still got 401 -
        // stop looping and let the error surface.
        console.error('Repeated 401 after relogin - not redirecting again')
      } else {
        console.error('Unauthorized request - token may be expired')
        markReloginAttempt()
        notifySessionExpired()
      }
    }
    return Promise.reject(error)
  }
)

export default instance
